import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ApolloCache } from '@apollo/client'
import { debounce, type DebouncedFunc } from 'lodash'
import { __RouterContext as RouterContext } from 'react-router'
import { encodeObjectForWaf } from 'src/lib/wafEncoding'
import { clearBrowserNoteDraft, readBrowserNoteDraft } from './browserDrafts'
import { pendingBrowserNoteDraft } from './migrateBrowserNoteDrafts'
import {
  clearNoteDraftEntry,
  getNoteDraftEntry,
  isNoteDraftConflict,
  isNoteDraftGone,
  serializeNoteDraft,
  updateNoteDraftEntry,
  useNoteDraftEntry,
  type NoteDraftSlot,
} from './noteDraftSession'
import {
  useImportNoteDraftMutation,
  useNoteDraftQuery,
  useSaveNoteDraftMutation,
  type NoteDraftQuery,
  type SaveNoteDraftMutationFn,
} from './noteDraft.generated'

const SAVE_DELAY_MS = 2000
const SAVE_MAX_WAIT_MS = 20000
const RETRY_MS = [2000, 5000, 10000, 30000]

const slotOf = ({ uid, type }: NoteDraftSlot) => `${uid}:${type}`

// Submit holds the slot so a save cannot start while the note is being filed.
const held = new Set<string>()
const savers = new Map<string, () => Promise<void>>()
const imports = new Map<string, Promise<void>>()
const importWakes = new Map<string, () => void>()
// Per note, not per form: a save after the form closed or switched patient sends this note's text, and saves run one
// at a time so each sends the revision the previous one returned.
const queued = new Map<string, unknown>()
const inflight = new Map<string, Promise<void>>()

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const asDate = (value: Date | string | null | undefined) => {
  if (!value) {
    return null
  }
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

const snapshotOf = (value: Record<string, unknown>) => {
  const snapshot = JSON.parse(JSON.stringify(value)) as Record<string, unknown>
  return { snapshot, sent: serializeNoteDraft(snapshot) }
}

// The save has to land before submit deletes the row. An insert that arrived afterwards would create the draft again.
export const settleNoteDraft = async (slot: NoteDraftSlot) => {
  const key = slotOf(slot)
  held.add(key)
  try {
    importWakes.get(key)?.()
    await imports.get(key)
    await savers.get(key)?.()
  } catch (error) {
    releaseNoteDraftHold(slot)
    throw error
  }
}

export const releaseNoteDraftHold = (slot: NoteDraftSlot) => {
  if (!held.delete(slotOf(slot))) {
    return
  }
  // The form may already differ from what was filed. Wake the saver now that the hold is gone.
  updateNoteDraftEntry(slot, {})
}

// The filed text becomes acked, so a form that is still open does not save the same note again. The last save may have
// failed, so the form's latest text is what was filed, not the stored acked.
export const forgetSubmittedNoteDraft = (cache: ApolloCache<unknown>, slot: NoteDraftSlot) => {
  const latest = queued.get(slotOf(slot))
  const acked = latest !== undefined ? serializeNoteDraft(latest) : getNoteDraftEntry(slot).acked
  updateNoteDraftEntry(slot, {
    revision: null,
    status: 'idle',
    deletesOn: null,
    acked,
    savedAt: null,
    changed: false,
    failing: false,
  })
  cache.evict({ id: 'ROOT_QUERY', fieldName: 'noteDraft' })
  cache.evict({ id: 'ROOT_QUERY', fieldName: 'noteDrafts' })
  cache.gc()
}

export const resetNoteDraftRuntimeForTests = () => {
  held.clear()
  savers.clear()
  imports.clear()
  importWakes.clear()
  queued.clear()
  inflight.clear()
}

const useBrowserNoteDraftSettled = ({ uid, type }: NoteDraftSlot) => {
  const pending = pendingBrowserNoteDraft({ uid, type })
  const [settledPromise, setSettledPromise] = useState<Promise<void> | null>(null)

  useEffect(() => {
    if (!pending) {
      return undefined
    }
    let live = true
    pending
      .then(() => {
        if (live) {
          setSettledPromise(pending)
        }
      })
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [pending])

  return pending === null || pending === settledPromise
}

type OpenedNoteDraft = {
  server: NoteDraftQuery['noteDraft']
  browser: Record<string, unknown> | null
}

const useSeedOpenedNoteDraft = ({ uid, type, opened }: NoteDraftSlot & { opened: OpenedNoteDraft | null }) => {
  useEffect(() => {
    if (!opened) {
      return
    }
    if (opened.server) {
      updateNoteDraftEntry(
        { uid, type },
        {
          revision: opened.server.revision,
          deletesOn: asDate(opened.server.deletesOn),
          acked: serializeNoteDraft(opened.server.value),
          status: 'idle',
          savedAt: asDate(opened.server.updatedAt),
        }
      )
      return
    }
    if (opened.browser) {
      updateNoteDraftEntry({ uid, type }, { revision: null, status: 'pending', acked: null, deletesOn: null })
      return
    }
    clearNoteDraftEntry({ uid, type })
  }, [opened, type, uid])
}

export const useLoadedNoteDraft = ({ uid, type }: NoteDraftSlot) => {
  const settled = useBrowserNoteDraftSettled({ uid, type })

  // network-only: the cached first fetch is not a current draft. Autosave never writes this query.
  const {
    data,
    loading,
    error: loadError,
  } = useNoteDraftQuery({
    variables: { uid, type },
    skip: !settled || !uid,
    fetchPolicy: 'network-only',
  })
  const server = settled && uid && !loading && !loadError ? data?.noteDraft ?? null : undefined
  const browser = server === null ? readBrowserNoteDraft({ uid, type }) : null
  const ready = server !== undefined
  const slot = slotOf({ uid, type })
  // Open from this result. A later one must not replace text already on screen.
  const opened = useRef<{ slot: string; server: typeof server; browser: typeof browser } | null>(null)
  if (ready && opened.current?.slot !== slot) {
    opened.current = { slot, server, browser }
  }
  const frozen = opened.current?.slot === slot ? opened.current : null

  const [importDraft] = useImportNoteDraftMutation()

  useSeedOpenedNoteDraft({ uid, type, opened: frozen })

  useEffect(() => {
    if (!frozen || frozen.server || !frozen.browser) {
      return undefined
    }
    const key = slotOf({ uid, type })
    const copied = frozen.browser
    let cancelled = false
    let attempt = 0

    /* eslint-disable no-await-in-loop */
    const task = (async () => {
      while (!cancelled) {
        try {
          const result = await importDraft({
            variables: { uid, type, value: encodeObjectForWaf(copied) },
          })
          if (cancelled) {
            return
          }
          const payload = result.data?.importNoteDraft
          if (!payload) {
            throw new Error('Note draft import returned no draft')
          }
          if (payload.imported || serializeNoteDraft(payload.draft.value) === serializeNoteDraft(copied)) {
            clearBrowserNoteDraft(`PatientHistoryForm-${uid}-${type}`)
            updateNoteDraftEntry(
              { uid, type },
              {
                status: 'saved',
                revision: payload.draft.revision,
                deletesOn: asDate(payload.draft.deletesOn),
                acked: serializeNoteDraft(copied),
                savedAt: new Date(),
              }
            )
            return
          }
          updateNoteDraftEntry({ uid, type }, { status: 'conflict', revision: payload.draft.revision })
          return
        } catch (error) {
          if (cancelled || held.has(key)) {
            return
          }
          if (isNoteDraftConflict(error)) {
            updateNoteDraftEntry({ uid, type }, { status: 'conflict' })
            return
          }
          updateNoteDraftEntry({ uid, type }, { status: 'error' })
          const wait = RETRY_MS[Math.min(attempt, RETRY_MS.length - 1)]
          attempt += 1
          await new Promise<void>((resolve) => {
            let timer = 0
            const finish = () => {
              window.clearTimeout(timer)
              if (importWakes.get(key) === finish) {
                importWakes.delete(key)
              }
              resolve()
            }
            importWakes.set(key, finish)
            timer = window.setTimeout(finish, wait)
          })
          if (cancelled || held.has(key)) {
            return
          }
        }
      }
    })()
    /* eslint-enable no-await-in-loop */

    imports.set(key, task)
    task
      .finally(() => {
        if (imports.get(key) === task) {
          imports.delete(key)
        }
      })
      .catch(() => undefined)

    return () => {
      cancelled = true
      importWakes.get(key)?.()
    }
  }, [frozen, importDraft, type, uid])

  const openedDraft = (frozen?.server?.value as Record<string, unknown> | undefined) ?? frozen?.browser ?? null

  return {
    ready: frozen !== null,
    failed: frozen === null && Boolean(loadError),
    draft: openedDraft,
    fromBrowser: Boolean(frozen && !frozen.server && frozen.browser),
  }
}

type NoteDraftDrainControls = {
  retry: { current: number }
  timer: { current: number | undefined }
}

const drainNoteDraft = async (slot: NoteDraftSlot, save: SaveNoteDraftMutationFn, controls: NoteDraftDrainControls) => {
  const { uid, type } = slot
  const { retry, timer } = controls
  const key = slotOf(slot)
  /* eslint-disable no-await-in-loop */
  // The running save may have read the values before the latest change.
  let running = inflight.get(key)
  while (running) {
    await running
    running = inflight.get(key)
  }
  const job = (async () => {
    for (;;) {
      // The browser import makes the first insert. The autosave effect saves once it lands.
      if (imports.has(key)) {
        return
      }
      const raw = queued.get(key)
      if (!isRecord(raw)) {
        return
      }
      const { snapshot, sent } = snapshotOf(raw)
      const current = getNoteDraftEntry({ uid, type })
      if (
        current.status === 'conflict' ||
        current.status === 'gone' ||
        (current.acked === sent && current.status !== 'error')
      ) {
        return
      }
      updateNoteDraftEntry({ uid, type }, { status: 'pending' })
      try {
        const result = await save({
          variables: {
            uid,
            type,
            value: encodeObjectForWaf(snapshot),
            ...(current.revision != null ? { expectedRevision: current.revision } : {}),
          },
        })
        const saved = result.data?.saveNoteDraft
        if (!saved) {
          throw new Error('Note draft save returned no draft')
        }
        retry.current = 0
        updateNoteDraftEntry(
          { uid, type },
          {
            status: 'saved',
            revision: saved.revision,
            deletesOn: asDate(saved.deletesOn),
            acked: sent,
            savedAt: new Date(),
          }
        )
        clearBrowserNoteDraft(`PatientHistoryForm-${uid}-${type}`)
      } catch (error) {
        if (isNoteDraftConflict(error)) {
          updateNoteDraftEntry({ uid, type }, { status: 'conflict' })
          return
        }
        // The text stays on screen. Saving stops, since a new insert would restart a deleted draft.
        if (isNoteDraftGone(error)) {
          updateNoteDraftEntry({ uid, type }, { status: 'gone', deletesOn: null })
          return
        }
        updateNoteDraftEntry({ uid, type }, { status: 'error' })
        if (held.has(key)) {
          return
        }
        const wait = RETRY_MS[Math.min(retry.current, RETRY_MS.length - 1)]
        retry.current += 1
        timer.current = window.setTimeout(() => {
          if (getNoteDraftEntry({ uid, type }).status === 'error') {
            drainNoteDraft(slot, save, controls).catch(() => undefined)
          }
        }, wait)
        return
      }
    }
  })()
  /* eslint-enable no-await-in-loop */
  inflight.set(key, job)
  try {
    await job
  } finally {
    if (inflight.get(key) === job) {
      inflight.delete(key)
    }
  }
}

const useSeedAckedNoteDraft = ({ uid, type }: NoteDraftSlot, values: unknown) => {
  const seeded = useRef(false)

  useEffect(() => {
    if (seeded.current) {
      return
    }
    seeded.current = true
    const current = getNoteDraftEntry({ uid, type })
    if (current.acked === null && current.status === 'idle') {
      updateNoteDraftEntry({ uid, type }, { acked: serializeNoteDraft(values) })
    }
  }, [type, uid, values])
}

const useNoteDraftSaver = ({ uid, type }: NoteDraftSlot) => {
  const [save] = useSaveNoteDraftMutation()
  const key = slotOf({ uid, type })
  const retry = useRef(0)
  const timer = useRef<number>()
  const debouncedRef = useRef<{ cancel: () => void }>({ cancel: () => undefined })

  const drain = useCallback(() => drainNoteDraft({ uid, type }, save, { retry, timer }), [save, type, uid])

  const flush = useCallback(async () => {
    debouncedRef.current.cancel()
    if (timer.current) {
      window.clearTimeout(timer.current)
    }
    await drain()
  }, [drain])

  const debounced = useMemo(() => debounce(() => flush(), SAVE_DELAY_MS, { maxWait: SAVE_MAX_WAIT_MS }), [flush])
  debouncedRef.current = debounced

  useEffect(() => {
    savers.set(key, flush)
    return () => {
      if (savers.get(key) === flush) {
        savers.delete(key)
      }
    }
  }, [flush, key])

  useEffect(
    () => () => {
      if (timer.current) {
        window.clearTimeout(timer.current)
      }
      Promise.resolve(debounced.flush()).catch(() => undefined)
    },
    [debounced]
  )

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        Promise.resolve(debounced.flush()).catch(() => undefined)
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => document.removeEventListener('visibilitychange', onVisibilityChange)
  }, [debounced])

  useEffect(() => {
    const onOnline = () => {
      if (getNoteDraftEntry({ uid, type }).status === 'error') {
        drain().catch(() => undefined)
      }
    }
    window.addEventListener('online', onOnline)
    return () => window.removeEventListener('online', onOnline)
  }, [drain, type, uid])

  return { debounced }
}

const useScheduleNoteDraftSave = ({
  uid,
  type,
  values,
  enabled,
  submitting,
  debounced,
}: NoteDraftSlot & {
  values: unknown
  enabled: boolean
  submitting: boolean
  debounced: DebouncedFunc<() => Promise<void>>
}) => {
  const key = slotOf({ uid, type })
  const entry = useNoteDraftEntry({ uid, type })

  // No cancel between keystrokes: it would restart the cap, and a steady typist would never save.
  useEffect(() => {
    // The browser import makes the first insert. Saving here too would look like another window.
    const importing = entry.revision == null && entry.status === 'pending'
    const stopped = entry.status === 'conflict' || entry.status === 'gone'
    const markChanged = (changed: boolean) => {
      if (entry.changed !== changed) {
        updateNoteDraftEntry({ uid, type }, { changed })
      }
    }
    if (!enabled || submitting || held.has(key) || stopped || importing) {
      debounced.cancel()
      markChanged(false)
      return undefined
    }
    if (entry.acked === serializeNoteDraft(values)) {
      markChanged(false)
      return undefined
    }
    markChanged(true)
    const scheduled = debounced()
    if (scheduled) {
      Promise.resolve(scheduled).catch(() => undefined)
    }
    return undefined
  }, [debounced, enabled, entry, key, submitting, type, uid, values])
}

const useChangedOnce = (changed: boolean, slot: NoteDraftSlot) => {
  const key = slotOf(slot)
  const [changedKey, setChangedKey] = useState<string | null>(null)
  if (changed && changedKey !== key) {
    setChangedKey(key)
  }
  return changed || changedKey === key
}

export const useNoteDraftAutosave = ({
  uid,
  type,
  values,
  enabled,
  submitting = false,
}: NoteDraftSlot & { values: unknown; enabled: boolean; submitting?: boolean }) => {
  useSeedAckedNoteDraft({ uid, type }, values)

  useEffect(() => {
    queued.set(slotOf({ uid, type }), values)
  }, [type, uid, values])

  const { debounced } = useNoteDraftSaver({ uid, type })
  const changed = useChangedOnce(enabled, { uid, type })
  useScheduleNoteDraftSave({ uid, type, values, enabled: changed, submitting, debounced })
}

const LEAVE_MESSAGE = 'This note is not saved. If you leave, the changes will be lost.'

// A form that rebuilds its values around the draft, such as Advanced SpecForm adding defaults and prefill, can differ
// from acked before anything is typed. Such a form passes dirty so it only warns once the clinician has changed it.
export const useUnsavedNoteDraftPrompt = ({
  uid,
  type,
  values,
  dirty = true,
}: NoteDraftSlot & { values: unknown; dirty?: boolean }) => {
  const entry = useNoteDraftEntry({ uid, type })
  const changed = useChangedOnce(dirty, { uid, type })
  const unsaved =
    entry.status === 'error' ||
    entry.status === 'conflict' ||
    entry.status === 'gone' ||
    entry.status === 'pending' ||
    (changed && entry.acked !== null && serializeNoteDraft(values) !== entry.acked)
  const router = useContext(RouterContext)

  useEffect(() => {
    if (!unsaved) {
      return undefined
    }
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    const unblock =
      router && typeof router.history?.block === 'function' ? router.history.block(LEAVE_MESSAGE) : undefined
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload)
      unblock?.()
    }
  }, [router, unsaved])
}
